import React from "react";

import styled from "veneer/styled";
import { Colors } from "veneer/Theme";

interface OperationScheduleCellProps {
  day: number;
  disabled?: boolean;
  hour: number;
  hoverStatus?: "selecting" | "deselecting" | false;
  isSelected?: boolean;
  onMouseDown: () => void;
  onMouseUp: () => void;
  onHover?: () => void;
  readOnly?: boolean;
}

const CellButton = styled("button", {
  shouldForwardProp: (prop) =>
    !["readOnly", "selected", "hoverStatus"].includes(prop as string),
})<{
  readOnly?: boolean;
  selected?: boolean;
  hoverStatus?: "selecting" | "deselecting" | false;
}>(({ readOnly, selected, hoverStatus, theme }) => ({
  minWidth: "fit-content",
  margin: theme.spacing(0.25),
  padding: theme.spacing(0.5),
  border: `1px solid ${Colors.blue700}`,
  borderRadius: theme.spacing(0.5),
  cursor: readOnly ? "default" : "pointer",
  fontSize: theme.typography.body2.fontSize,

  ...(selected && {
    backgroundColor: Colors.blue700,
    color: Colors.white,

    "&:focus, &:hover, &:active": {
      backgroundColor: Colors.blue700,
      color: Colors.white,
    },
  }),

  ...(hoverStatus === "selecting" && {
    backgroundColor: Colors.blue700,
    color: Colors.black,
  }),

  ...(hoverStatus === "deselecting" && {
    backgroundColor: Colors.gray200,
    color: Colors.black,
  }),

  ...(readOnly && {
    pointerEvents: "none",
    opacity: 0.5,
  }),
}));

function OperationScheduleCell({
  disabled,
  hour,
  hoverStatus,
  isSelected,
  readOnly,
  onMouseDown,
  onMouseUp,
  onHover,
}: OperationScheduleCellProps) {
  const isDisabled = disabled || readOnly;

  function handleMouseDown() {
    if (isDisabled) {
      return;
    }
    onMouseDown();
  }

  function handleMouseEnter() {
    if (isDisabled) {
      return;
    }
    onHover?.();
  }

  function handleMouseUp() {
    if (isDisabled) {
      return;
    }
    onMouseUp();
  }

  return (
    <CellButton
      data-testid={`op-sched-cell-${hour}`}
      disabled={disabled}
      hoverStatus={hoverStatus}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseUp={handleMouseUp}
      readOnly={readOnly}
      selected={isSelected}
      type="button"
    >
      {`${hour}:00`}
    </CellButton>
  );
}

export { OperationScheduleCell };
export default OperationScheduleCell;
